import {
    AppDataTable,
    createAppColumnHelper,
    createNameSortFn,
    defaultNameSortMode,
    getNameSortingState,
    useAppTable,
    type NameSortMode,
} from '@features/data-table'
import { Badge, Chip, Group, Modal, Stack, Text } from '@mantine/core'
import type { SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import type {
    ResolvedCommunicationRecipient,
    ResolvedCommunicationRecipientKind,
    ResolvedCommunicationStudent,
} from '../types'

export type RecipientResolutionModalView =
    | { type: 'students' }
    | { type: 'recipients'; bucket: 'to' | 'cc' | 'bcc'; label: 'To' | 'Cc' | 'Bcc' }

type RecipientResolutionModalProps = {
    view: RecipientResolutionModalView | null
    onClose: () => void
    students: ResolvedCommunicationStudent[]
    recipients: {
        to: ResolvedCommunicationRecipient[]
        cc: ResolvedCommunicationRecipient[]
        bcc: ResolvedCommunicationRecipient[]
    }
    loading: boolean
}

const studentColumnHelper = createAppColumnHelper<ResolvedCommunicationStudent>()
const recipientColumnHelper = createAppColumnHelper<ResolvedCommunicationRecipient>()

const recipientKindOptions: Array<{
    value: ResolvedCommunicationRecipientKind
    label: string
    color: string
}> = [
    { value: 'student', label: 'Students', color: 'teal' },
    { value: 'staff', label: 'Staff', color: 'blue' },
    { value: 'parent', label: 'Parents', color: 'grape' },
    { value: 'custom', label: 'Custom', color: 'yellow' },
]

export function RecipientResolutionModal({ view, onClose, students, recipients, loading }: RecipientResolutionModalProps) {
    const title = view?.type === 'students' ? 'Selected students' : `${view?.label ?? ''} recipients`

    return (
        <Modal opened={view !== null} onClose={onClose} title={title} size="xl" centered>
            {view?.type === 'students' ? (
                <StudentsTable students={students} loading={loading} />
            ) : view?.type === 'recipients' ? (
                <RecipientsTable
                    bucket={view.bucket}
                    recipients={recipients[view.bucket]}
                    loading={loading}
                />
            ) : null}
        </Modal>
    )
}

function StudentsTable({ students, loading }: { students: ResolvedCommunicationStudent[]; loading: boolean }) {
    const [nameSortMode, setNameSortMode] = useState<NameSortMode>(defaultNameSortMode)
    const [sorting, setSorting] = useState<SortingState>([])
    const setNameSort = (mode: NameSortMode) => {
        setNameSortMode(mode)
        setSorting(getNameSortingState(mode))
    }
    const columns = useMemo(() => studentColumnHelper.columns([
        studentColumnHelper.accessor((student) => `${student.firstName} ${student.surname}`.trim(), {
            id: 'name',
            header: 'Name',
            size: 440,
            sortFn: createNameSortFn(nameSortMode, {
                getFirstName: (student) => student.firstName,
                getSurname: (student) => student.surname,
            }),
            meta: {
                label: 'Name',
                variant: 'text',
                nameSorting: { mode: nameSortMode, onModeChange: setNameSort },
            },
        }),
        studentColumnHelper.accessor('studentNumber', {
            header: 'Student number',
            size: 150,
            meta: { label: 'Student number', variant: 'number' },
        }),
    ]), [nameSortMode])

    const table = useAppTable({
        tableKey: 'communication-selected-students',
        persistState: false,
        data: students,
        columns,
        getRowId: (student) => String(student.studentNumber),
        enableSorting: true,
        state: { sorting },
        onSortingChange: setSorting,
    })

    return (
        <Stack h={560} mih={0}>
            <AppDataTable
                table={table}
                tableKey="communication-selected-students"
                toolbarOptions={{ search: true, filter: false, sort: true, view: false, moreOptions: false }}
                emptyState="No students have been selected."
                loading={loading}
                loadingState={<LoadingIndicator resource="students" groupProps={{ justify: 'center', w: '100%' }} />}
                minWidth={600}
                maxHeight="100%"
                tableProps={{ verticalSpacing: 0, horizontalSpacing: 'xs', fz: 'xs' }}
                virtualization={{ enabled: true, estimateSize: 30, overscan: 18 }}
            />
        </Stack>
    )
}

function RecipientsTable({ bucket, recipients, loading }: {
    bucket: 'to' | 'cc' | 'bcc'
    recipients: ResolvedCommunicationRecipient[]
    loading: boolean
}) {
    const [enabledKinds, setEnabledKinds] = useState<ResolvedCommunicationRecipientKind[]>(
        recipientKindOptions.map((option) => option.value),
    )
    const [nameSortMode, setNameSortMode] = useState<NameSortMode>(defaultNameSortMode)
    const [sorting, setSorting] = useState<SortingState>([])
    const setNameSort = (mode: NameSortMode) => {
        setNameSortMode(mode)
        setSorting(getNameSortingState(mode))
    }
    const filteredRecipients = useMemo(
        () => recipients.filter((recipient) => enabledKinds.includes(recipient.kind)),
        [enabledKinds, recipients],
    )
    const counts = useMemo(() => Object.fromEntries(recipientKindOptions.map((option) => [
        option.value,
        recipients.filter((recipient) => recipient.kind === option.value).length,
    ])), [recipients]) as Record<ResolvedCommunicationRecipientKind, number>

    const columns = useMemo(() => recipientColumnHelper.columns([
        recipientColumnHelper.accessor((recipient) => `${recipient.firstName} ${recipient.surname}`.trim(), {
            id: 'name',
            header: 'Name',
            size: 290,
            sortFn: createNameSortFn(nameSortMode, {
                getFirstName: (recipient) => recipient.firstName,
                getSurname: (recipient) => recipient.surname,
            }),
            meta: {
                label: 'Name',
                variant: 'text',
                nameSorting: { mode: nameSortMode, onModeChange: setNameSort },
            },
        }),
        recipientColumnHelper.accessor('emailAddress', {
            header: 'Email address',
            size: 280,
            meta: { label: 'Email address', variant: 'text', truncate: true },
        }),
        recipientColumnHelper.accessor('kind', {
            header: 'Type',
            size: 100,
            meta: { label: 'Type', variant: 'select' },
            cell: (context) => {
                const option = recipientKindOptions.find((item) => item.value === context.getValue())
                return <Badge color={option?.color} variant="light" size="xs" tt="capitalize">{context.getValue()}</Badge>
            },
        }),
    ]), [nameSortMode])

    const table = useAppTable({
        tableKey: `communication-${bucket}-recipients`,
        persistState: false,
        data: filteredRecipients,
        columns,
        getRowId: (recipient) => `${recipient.kind}:${recipient.emailAddress}`,
        enableSorting: true,
        state: { sorting },
        onSortingChange: setSorting,
    })

    return (
        <Stack h={600} mih={0} gap="sm">
            <Stack gap={5}>
                <Text size="xs" fw={500}>Recipient type</Text>
                <Chip.Group
                    multiple
                    value={enabledKinds}
                    onChange={(values) => setEnabledKinds(values as ResolvedCommunicationRecipientKind[])}
                >
                    <Group gap={6}>
                        {recipientKindOptions.map((option) => (
                            <Chip key={option.value} value={option.value} color={option.color} variant="light" size="xs">
                                {option.label} ({counts[option.value]})
                            </Chip>
                        ))}
                    </Group>
                </Chip.Group>
            </Stack>
            <AppDataTable
                table={table}
                tableKey={`communication-${bucket}-recipients`}
                toolbarOptions={{ search: true, filter: false, sort: true, view: false, moreOptions: false }}
                emptyState={recipients.length === 0 ? 'No recipients have been resolved.' : 'No recipients match the selected types.'}
                loading={loading}
                loadingState={<LoadingIndicator resource="recipients" groupProps={{ justify: 'center', w: '100%' }} />}
                minWidth={670}
                maxHeight="100%"
                tableProps={{ verticalSpacing: 0, horizontalSpacing: 'xs', fz: 'xs' }}
                virtualization={{ enabled: true, estimateSize: 30, overscan: 18 }}
            />
        </Stack>
    )
}
